import React, { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Col, Row } from "react-bootstrap";
import { FaUserEdit } from "react-icons/fa";
import { FaCircleXmark } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import DeleteModal from "../../Components/Modals/DeleteModal";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import useServiceController from "../../controllers/ServiceControllers/useServiceController";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/service_management.module.scss";

function getServiceName(row) {
  return (
    row.name ??
    row.title ??
    row.service_name ??
    (row.service && row.service.name) ??
    "-"
  );
}

const ServiceManagement = () => {
  const {
    values: { list: rowData, loading, saving },
    functions: {
      handleCreateService,
      handleUpdateService,
      handleDeleteService,
    },
  } = useServiceController();

  const [rowDataOptimistic, setRowDataOptimistic] = useState([]);
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [editedData, setEditedData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    if (loading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No services found");
  }, [loading, rowData.length]);

  useEffect(() => {
    setRowDataOptimistic(rowData);
  }, [rowData]);

  const handleDeleteModalOpen = () => setDeleteModal(true);
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData(null);
  };

  const handleEditModalOpen = () => setEditModal(true);
  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData(null);
    setName("");
  };

  const handleAddModalOpen = () => {
    setName("");
    setAddModal(true);
  };
  const handleAddModalClose = () => {
    setAddModal(false);
    setName("");
  };

  const onConfirmDelete = async (id) => {
    try {
      await handleDeleteService(id);
      handleDeleteModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const openEdit = (row) => {
    setEditedData(row);
    setName(getServiceName(row) === "-" ? "" : getServiceName(row));
    handleEditModalOpen();
  };

  const handleSaveAdd = async () => {
    if (!name.trim()) {
      Toast("Please enter a service name", "error");
      return;
    }
    try {
      await handleCreateService({
        title: name.trim(),
      });
      handleAddModalClose();
    } catch {
    }
  };

  const handleSaveEdit = async () => {
    if (!editedData?.id) {
      return;
    }
    if (!name.trim()) {
      Toast("Please enter a service name", "error");
      return;
    }
    try {
      await handleUpdateService(editedData.id, {
        title: name.trim(),
      });
      handleEditModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  function indication() {
    return indicationMessage;
  }

  const displayRows = rowDataOptimistic;

  const columns = [
    {
      dataField: "id",
      text: "ID",
      sort: true,
      headerStyle: { width: "100px" },
    },
    {
      dataField: "name",
      text: "Name",
      sort: true,
      headerStyle: { minWidth: "320px" },
      formatter: (_cell, row) => getServiceName(row),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "220px" },
      formatter: (_cell, row) => {
        if (row.id == null) return null;
        return (
          <div className="d-flex justify-content-center align-items-center">
            <div
              className={`${styles.btnBg} d-flex justify-content-center align-items-center rounded-3 mx-2 p-2`}
              role="button"
              onClick={() => openEdit(row)}
            >
              <span className="align-items-center d-flex justify-content-center pe-1">
                <FaUserEdit />
              </span>
              <span>Edit</span>
            </div>
            <div
              className="d-flex justify-content-center align-items-center bg-danger rounded-3 p-2 mx-2"
              role="button"
              onClick={() => {
                setEditedData({
                  ...row,
                  name: getServiceName(row),
                });
                handleDeleteModalOpen();
              }}
            >
              <span className="d-flex align-items-center pe-1">
                <MdDeleteForever />
              </span>
              <span>Delete</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {deleteModal ? (
        <DeleteModal
          handleDeleteModalClose={handleDeleteModalClose}
          data={editedData}
          onDelete={onConfirmDelete}
        />
      ) : null}

      {editModal ? (
        <ModalContainer handleClose={handleEditModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Edit Service</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleEditModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Name</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    placeholder="Service name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-4"
                    text={saving ? "Saving..." : "Update Service"}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      {addModal ? (
        <ModalContainer handleClose={handleAddModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Add Service</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleAddModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Name</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    placeholder="Service name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-4"
                    text={saving ? "Saving..." : "Save Service"}
                    onClick={handleSaveAdd}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text="Services Management" />
          <AddButton text="Add Service" onClick={handleAddModalOpen} />
        </div>
      </div>

      <div className={styles.mainTable}>
        <Table data={displayRows} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default ServiceManagement;
